<script setup lang="ts">
/**
 * The content of a notification panel: a head with the title, the unread
 * count and „Alle gelesen“, the latest items as `NxFeedItem` rows, and
 * loading, error and empty states. `NxNotificationBell` renders it in its
 * popover; a host that brings its own trigger and popover (a NavBar entry's
 * `panel`) renders it directly.
 *
 * Data stays with the caller: pass `items` (and `unreadCount` when the server
 * knows more than the list shows), listen to `open` (a row was chosen),
 * `mark-all` and `retry`. With `hasMore`, the end of the list pages through
 * `NxLoadMore` (`auto`: on scrolling, and on its button); bind the loader with
 * `@load-more` — it may return a promise.
 *
 * Arrow keys, Home and End walk the rows — except inside an open menu or
 * popover of a row (an `NxMenuButton` in `#item-actions`), which walks its
 * own entries, and on a control that used the key itself (a menu trigger
 * opening on ArrowDown). When a row goes (read or deleted)
 * while focus was in it, focus moves to the row now in its place, else the
 * one before, else the panel; after a pressed „Weitere laden“ it lands on the
 * first new row. Loading is announced as a status, an error as an alert.
 *
 * Its parts keep the bell's class names (`nx-notification-bell__*`), so
 * callers and tests that address them keep working.
 */
import { computed, nextTick, useId, useTemplateRef, watch } from 'vue';
import Bell from 'lucide-vue-next/dist/esm/icons/bell.js'; import CheckCheck from 'lucide-vue-next/dist/esm/icons/check-check.js';
import NxLoadMore from '../LoadMore/NxLoadMore.vue';
import NxFeedItem from './NxFeedItem.vue';
import type { NxFeedEntry } from './types';

const props = withDefaults(
  defineProps<{
    items?: readonly NxFeedEntry[];
    /** Unread total; default: the unread rows in `items`. */
    unreadCount?: number;
    loading?: boolean;
    /** An error text; shown with a retry button while there are no items. */
    error?: string;
    /** Another page exists: „Weitere laden“ under the list. */
    hasMore?: boolean;
    /** Loads the next page (`@load-more`); may return a promise. */
    onLoadMore?: () => unknown;
    /** Relative-time locale of the rows; default: the app's. */
    locale?: string;
    label?: string;
    markAllLabel?: string;
    loadingLabel?: string;
    retryLabel?: string;
    emptyTitle?: string;
    emptyHint?: string;
    loadMoreLabel?: string;
    loadMoreErrorLabel?: string;
    /** Spoken before the title of an unread row. */
    itemUnreadLabel?: string;
  }>(),
  {
    items: () => [],
    unreadCount: undefined,
    loading: false,
    error: undefined,
    hasMore: false,
    onLoadMore: undefined,
    locale: undefined,
    label: 'Benachrichtigungen',
    markAllLabel: 'Alle gelesen',
    loadingLabel: 'Lädt …',
    retryLabel: 'Erneut versuchen',
    emptyTitle: 'Alles gelesen',
    emptyHint: 'Neue Erwähnungen und Hinweise erscheinen hier.',
    loadMoreLabel: 'Weitere laden',
    loadMoreErrorLabel: 'Laden fehlgeschlagen',
    itemUnreadLabel: 'Ungelesen',
  }
);

const emit = defineEmits<{
  /** A row was chosen. */
  open: [item: NxFeedEntry, event: MouseEvent];
  'mark-all': [];
  retry: [];
}>();

defineSlots<{
  /** Replaces one row. */
  item?(props: { item: NxFeedEntry }): unknown;
  /** Row actions of each row, on hover and focus (see `NxFeedItem#actions`). */
  'item-actions'?(props: { item: NxFeedEntry }): unknown;
  /** Always visible actions of each row (see `NxFeedItem#primary-actions`). */
  'item-primary-actions'?(props: { item: NxFeedEntry }): unknown;
  /** Replaces a row's excerpt text (see `NxFeedItem#excerpt`). */
  'item-excerpt'?(props: { item: NxFeedEntry }): unknown;
  /** Replaces a row's avatar, e.g. a module icon (see `NxFeedItem#icon`). */
  'item-icon'?(props: { item: NxFeedEntry }): unknown;
  /** Extra header buttons next to „Alle gelesen“. */
  'header-actions'?(): unknown;
  /** Replaces the empty state. */
  empty?(): unknown;
  /** Under the list, e.g. „Alle anzeigen“. */
  footer?(): unknown;
}>();

const titleId = useId();
const root = useTemplateRef<HTMLElement>('root');
const list = useTemplateRef<HTMLElement>('list');
const more = useTemplateRef<HTMLElement>('more');

const unread = computed(
  () => props.unreadCount ?? props.items.reduce((n, item) => n + (item.unread ? 1 : 0), 0)
);
const badge = computed(() =>
  unread.value > 0 ? (unread.value > 99 ? '99+' : String(unread.value)) : ''
);

const state = computed<'loading' | 'error' | 'empty' | 'list'>(() => {
  if (props.items.length) return 'list';
  if (props.loading) return 'loading';
  if (props.error) return 'error';
  return 'empty';
});

/** The rows' click targets, in order. */
function rowTargets(): HTMLElement[] {
  return Array.from(root.value?.querySelectorAll<HTMLElement>('.nx-feed-item__main') ?? []);
}

/** Menus and popovers inside a row walk their own entries with the arrow keys. */
const OWN_KEYS = '[role="menu"], [role="listbox"], [role="dialog"], [popover]';

/** Whether the key comes from a menu or popover inside the panel, or was used already. */
function keyIsTaken(e: KeyboardEvent): boolean {
  if (e.defaultPrevented) return true;
  const from = (e.target as Element | null)?.closest?.(OWN_KEYS);
  return !!from && from !== root.value && !!root.value?.contains(from);
}

/** Arrow keys walk the row targets; Home/End jump to the ends. */
function onKeydown(e: KeyboardEvent) {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
  if (keyIsTaken(e)) return;
  const rows = rowTargets();
  if (!rows.length) return;
  e.preventDefault();
  const at = rows.indexOf(document.activeElement as HTMLElement);
  let next = 0;
  if (e.key === 'End') next = rows.length - 1;
  else if (e.key === 'ArrowDown') next = at < 0 ? 0 : Math.min(at + 1, rows.length - 1);
  else if (e.key === 'ArrowUp') next = at < 0 ? rows.length - 1 : Math.max(at - 1, 0);
  rows[next]?.focus();
}

// A row that goes takes the focus inside it to the page: note which row had
// it before the list changes, and hand it on once the list has.
watch(
  () => props.items,
  () => {
    const rows = Array.from(list.value?.children ?? []);
    const active = document.activeElement;
    const at = rows.findIndex((row) => row.contains(active));
    if (at < 0) return;
    void nextTick(() => {
      const now = document.activeElement;
      if (now && now !== document.body && root.value?.contains(now)) return;
      const targets = rowTargets();
      (targets[Math.min(at, targets.length - 1)] ?? root.value)?.focus();
    });
  },
  { flush: 'pre' }
);

/** NxLoadMore's loader; a press hands focus to the first new row. */
async function loadMore() {
  const before = props.items.length;
  const pressed = !!more.value?.contains(document.activeElement);
  await props.onLoadMore?.();
  await nextTick();
  if (pressed) (rowTargets()[before] ?? root.value)?.focus();
}

defineExpose({
  /** Moves focus to the panel, e.g. when it opens. */
  focus(options?: FocusOptions) {
    root.value?.focus(options);
  },
});
</script>

<template>
  <div
    ref="root"
    class="nx-notification-panel nx-notification-bell__panel"
    tabindex="-1"
    :aria-labelledby="titleId"
    data-testid="nx-notification-panel"
    @keydown="onKeydown"
  >
    <header class="nx-notification-bell__head">
      <h2 :id="titleId" class="nx-notification-bell__title">{{ label }}</h2>
      <span v-if="badge" class="nx-notification-bell__count" aria-hidden="true">
        {{ badge }}
      </span>
      <span class="nx-notification-bell__head-actions">
        <slot name="header-actions" />
        <button
          v-if="unread > 0"
          type="button"
          class="nx-notification-bell__markall"
          data-testid="nx-notification-bell-mark-all"
          @click="emit('mark-all')"
        >
          <CheckCheck class="nxt-icon-sm" :stroke-width="2.2" aria-hidden="true" />
          {{ markAllLabel }}
        </button>
      </span>
    </header>

    <div class="nx-notification-bell__body" :aria-busy="loading || undefined">
      <p v-if="state === 'loading'" class="nx-notification-bell__state" role="status">
        <span class="nx-notification-bell__spinner" aria-hidden="true" />
        {{ loadingLabel }}
      </p>
      <div v-else-if="state === 'error'" class="nx-notification-bell__state" role="alert">
        <p class="nx-notification-bell__error">{{ error }}</p>
        <button type="button" class="nx-notification-bell__link" @click="emit('retry')">
          {{ retryLabel }}
        </button>
      </div>
      <div v-else-if="state === 'empty'" class="nx-notification-bell__empty">
        <slot name="empty">
          <span class="nx-notification-bell__empty-icon" aria-hidden="true">
            <Bell class="nxt-icon-lg" :stroke-width="1.8" />
          </span>
          <p class="nx-notification-bell__empty-title">{{ emptyTitle }}</p>
          <p class="nx-notification-bell__empty-hint">{{ emptyHint }}</p>
        </slot>
      </div>
      <template v-else>
        <ul ref="list" class="nx-notification-bell__list" role="list">
          <li v-for="item in items" :key="item.id">
            <slot name="item" :item="item">
              <NxFeedItem
                :title="item.title"
                :excerpt="item.excerpt"
                :time="item.time"
                :unread="item.unread"
                :source="item.source"
                :count="item.count"
                :avatar="item.avatar"
                :href="item.href"
                :locale="locale"
                :unread-label="itemUnreadLabel"
                @open="emit('open', item, $event)"
              >
                <template v-if="$slots['item-icon']" #icon>
                  <slot name="item-icon" :item="item" />
                </template>
                <template v-if="$slots['item-excerpt']" #excerpt>
                  <slot name="item-excerpt" :item="item" />
                </template>
                <template v-if="$slots['item-actions']" #actions>
                  <slot name="item-actions" :item="item" />
                </template>
                <template v-if="$slots['item-primary-actions']" #primary-actions>
                  <slot name="item-primary-actions" :item="item" />
                </template>
              </NxFeedItem>
            </slot>
          </li>
        </ul>
        <div v-if="hasMore" ref="more" class="nx-notification-panel__more">
          <NxLoadMore
            auto
            :show-end="false"
            :label="loadMoreLabel"
            :loading-label="loadingLabel"
            :error-label="loadMoreErrorLabel"
            :retry-label="retryLabel"
            :locale="locale"
            @load="loadMore"
          />
        </div>
      </template>
    </div>

    <footer v-if="$slots.footer" class="nx-notification-bell__foot">
      <slot name="footer" />
    </footer>
  </div>
</template>

<style scoped>
.nx-notification-bell__panel {
  display: flex;
  flex-direction: column;
  max-height: min(calc(var(--nxt-size-unit) * 32), calc(100vh - calc(var(--nxt-size-unit) * 5)));
  outline: none;
}
.nx-notification-bell__head {
  flex: none;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-sm) var(--nxt-space-sm) calc(var(--nxt-space-xs) * 1.25)
    var(--nxt-space-md);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-notification-bell__title {
  margin: 0;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: -0.01em;
  color: var(--nxt-fg);
}
.nx-notification-bell__count {
  display: inline-grid;
  place-items: center;
  min-width: calc(var(--nxt-size-unit) * 1.25);
  height: calc(var(--nxt-size-unit) * 1.25);
  padding-inline: calc(var(--nxt-space-xs) * 0.75);
  box-sizing: border-box;
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-bold);
  font-variant-numeric: tabular-nums;
}
.nx-notification-bell__head-actions {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  margin-inline-start: auto;
}
.nx-notification-bell__markall {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--nxt-space-2xs) * 1.25);
  padding: calc(var(--nxt-space-2xs) * 1.25) var(--nxt-space-xs);
  border: 0;
  border-radius: var(--nxt-radius-md);
  background: transparent;
  color: var(--nxt-brand-subtle-fg);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  white-space: nowrap;
  cursor: pointer;
}
.nx-notification-bell__markall:hover {
  background: var(--nxt-brand-subtle-bg);
}
.nx-notification-bell__markall:focus-visible,
.nx-notification-bell__link:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}

.nx-notification-bell__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  padding: calc(var(--nxt-space-xs) * 0.75);
}
.nx-notification-bell__list {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: inherit;
}
.nx-notification-panel__more {
  margin-block-start: var(--nxt-space-2xs);
}

.nx-notification-bell__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nxt-space-xs);
  margin: 0;
  padding: var(--nxt-space-lg) var(--nxt-space-sm);
  text-align: center;
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-subtle);
}
.nx-notification-bell__error {
  margin: 0;
  color: var(--nxt-danger-subtle-fg);
}
.nx-notification-bell__link {
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--nxt-brand-subtle-fg);
  font: inherit;
  font-weight: var(--nxt-font-weight-semibold);
  text-decoration: underline;
  text-underline-offset: calc(var(--nxt-size-unit) * 0.125);
  cursor: pointer;
}
.nx-notification-bell__spinner {
  width: var(--nxt-icon-size-md);
  height: var(--nxt-icon-size-md);
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-border);
  border-block-start-color: var(--nxt-brand-bg);
  border-radius: var(--nxt-radius-full);
  animation: nx-notification-panel-spin 0.8s linear infinite;
}

.nx-notification-bell__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(var(--nxt-space-xs) * 0.75);
  padding: var(--nxt-space-xl) var(--nxt-space-md) var(--nxt-space-lg);
  text-align: center;
}
.nx-notification-bell__empty-icon {
  display: grid;
  place-items: center;
  width: var(--nxt-tile-size-md);
  height: var(--nxt-tile-size-md);
  margin-block-end: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-full);
  background: radial-gradient(
    circle at 30% 25%,
    color-mix(in srgb, var(--nxt-brand-bg) 18%, transparent),
    color-mix(in srgb, var(--nxt-brand-bg) 6%, transparent)
  );
  color: var(--nxt-brand-bg);
}
.nx-notification-bell__empty-title,
.nx-notification-bell__empty-hint {
  margin: 0;
}
.nx-notification-bell__empty-title {
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg);
}
.nx-notification-bell__empty-hint {
  max-width: var(--nxt-panel-width-md);
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg-subtle);
}

.nx-notification-bell__foot {
  flex: none;
  display: flex;
  justify-content: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-block-start: var(--nxt-hairline) solid var(--nxt-border-subtle);
  background: var(--nxt-surface-sunken-bg);
  font-size: var(--nxt-font-size-xs);
}

@keyframes nx-notification-panel-spin {
  to {
    transform: rotate(1turn);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-notification-bell__spinner {
    animation-duration: 2.4s;
  }
}
</style>
